import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  Calendar, 
  Filter, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  Clock,
  CheckCircle,
  BarChart3,
  Users,
  Shield,
  X
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Modal } from '../../../components/ui/Modal';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import MockDataService from '../../../services/MockDataService';
import toast from 'react-hot-toast';

const ComplianceReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [newReport, setNewReport] = useState({
    title: '',
    description: '',
    type: 'security',
    period: 'Q1 2024',
    auditor: '',
    scope: '',
    objectives: ''
  });

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getAuditReports();
      if (response.success) {
        setReports(response.data);
      }
    } catch (error) {
      console.error('Error loading compliance reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async () => {
    if (!newReport.title.trim()) {
      toast.error('Please enter a report title');
      return;
    }
    if (!newReport.description.trim()) {
      toast.error('Please enter a report description');
      return;
    }
    if (!newReport.auditor.trim()) {
      toast.error('Please enter an auditor name');
      return;
    }

    try {
      toast.loading('Creating report...', { id: 'create-report' });
      const response = await MockDataService.createAuditReport(newReport);
      if (response.success) {
        setReports([...reports, response.data]);
        setShowCreateModal(false);
        setNewReport({
          title: '',
          description: '',
          type: 'security',
          period: 'Q1 2024',
          auditor: '',
          scope: '',
          objectives: ''
        });
        toast.success('Report created successfully', { id: 'create-report' });
      }
    } catch (error) {
      console.error('Error creating compliance report:', error);
      toast.error('Failed to create report', { id: 'create-report' });
    }
  };

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setShowViewModal(true);
  };

  const handleGenerateReport = async (reportId) => {
    try {
      toast.loading('Generating report...', { duration: 2000 });
      setTimeout(() => {
        const updatedReports = reports.map(report => 
          report.id === reportId 
            ? { ...report, status: 'completed', generatedAt: new Date().toISOString() }
            : report
        );
        setReports(updatedReports);
        toast.success('Report generated successfully');
      }, 2000);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }
    
    try {
      toast.loading('Deleting report...', { id: 'delete-report' });
      setTimeout(() => {
        const updatedReports = reports.filter(report => report.id !== reportId);
        setReports(updatedReports);
        toast.success('Report deleted successfully', { id: 'delete-report' });
      }, 500);
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Failed to delete report', { id: 'delete-report' });
    }
  };

  const handleDownloadReport = (report) => {
    toast.loading('Preparing download...', { duration: 1500 });
    setTimeout(() => {
      toast.success(`Downloading report: ${report.title}`);
      //console.log('Downloading report:', report);
    }, 1500);
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || report.status === filterStatus;
    const matchesType = filterType === 'all' || report.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { 
        bgColor: 'bg-[#006600]', 
        textColor: 'text-white',
        icon: CheckCircle 
      },
      in_progress: { 
        bgColor: 'bg-[#0B2530]', 
        textColor: 'text-white',
        icon: Clock 
      },
      draft: { 
        bgColor: 'bg-[#FFE7E0]', 
        textColor: 'text-[#BB0000]',
        icon: Edit 
      }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon size={12} />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeConfig = {
      security: { 
        bgColor: 'bg-[#BB0000]', 
        textColor: 'text-white',
        icon: Shield 
      },
      compliance: { 
        bgColor: 'bg-[#006600]', 
        textColor: 'text-white',
        icon: FileText 
      },
      access: { 
        bgColor: 'bg-[#0B2530]', 
        textColor: 'text-white',
        icon: Users 
      },
      infrastructure: { 
        bgColor: 'bg-[#006600]', 
        textColor: 'text-white',
        icon: Shield 
      }
    };
    
    const config = typeConfig[type] || typeConfig.compliance;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon size={12} />
        {type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: '#006600' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0B2530' }}>Compliance Reports</h1>
          <p style={{ color: '#4A5B63' }}>Generate and manage compliance audit reports</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          Create Report
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 border-2 border-border">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={16} style={{ color: '#8A9AA2' }} />
            <Input
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button 
            variant="outline" 
            className="flex items-center gap-2 border-2 border-border hover:border-[#006600] hover:bg-[#F8FCFB]"
            onClick={() => setShowMoreFilters(true)}
          >
            <Filter size={16} />
            More Filters
            {(filterStatus !== 'all' || filterType !== 'all') && (
              <span className="ml-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" style={{ backgroundColor: '#006600' }}>
                {(filterStatus !== 'all' ? 1 : 0) + (filterType !== 'all' ? 1 : 0)}
              </span>
            )}
          </Button>
        </div>
      </Card>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.map((report) => (
          <Card key={report.id} className="p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="font-semibold" style={{ color: '#0B2530' }}>{report.title}</h3>
                <p className="text-sm mb-3" style={{ color: '#4A5B63' }}>{report.description}</p>
              </div>
              <div className="flex gap-2">
                {getStatusBadge(report.status)}
                {getTypeBadge(report.type)}
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm" style={{ color: '#4A5B63' }}>
                <Calendar size={14} />
                <span>Period: {report.period}</span>
              </div>
              <div className="flex items-center gap-2 text-sm" style={{ color: '#4A5B63' }}>
                <Users size={14} />
                <span>Auditor: {report.auditor}</span>
              </div>
              <div className="flex items-center gap-2 text-sm" style={{ color: '#4A5B63' }}>
                <BarChart3 size={14} />
                <span>Findings: {report.findingsCount}</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewReport(report)}
                className="flex-1 border-2 border-[#006600] text-[#006600] hover:bg-[#E8FCF7]"
                title="View Report"
              >
                <Eye size={14} className="mr-1" />
                View
              </Button>
              {report.status === 'draft' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleGenerateReport(report.id)}
                  className="flex-1 border-2 border-[#BB0000] text-[#BB0000] hover:bg-[#FFE7E0]"
                  title="Generate Report"
                >
                  <FileText size={14} className="mr-1" />
                  Generate
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteReport(report.id)}
                className="border-2 border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                title="Delete Report"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Create Report Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full mx-4 border-2 border-[#E5EFF3] dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b" style={{ borderColor: '#E5EFF3', background: 'linear-gradient(145deg, #FFFFFF, #F8FCFB)' }}>
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold" style={{ color: '#0B2530' }}>
                  Create Compliance Report
                </h2>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="transition-colors"
                  style={{ color: '#8A9AA2' }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-6 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Report Title */}
              <div className="pb-4 border-b" style={{ borderColor: '#E5EFF3' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                  Report Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newReport.title}
                  onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                  placeholder="Enter report title"
                  className="border-2 focus:border-[#006600]"
                />
              </div>
              
              {/* Description */}
              <div className="pb-4 border-b" style={{ borderColor: '#E5EFF3' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                  Description <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={newReport.description}
                  onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                  placeholder="Enter report description"
                  rows={3}
                  className="border-2 focus:border-[#006600]"
                />
              </div>

              {/* Report Type and Period */}
              <div className="grid grid-cols-2 gap-6 pb-4 border-b" style={{ borderColor: '#E5EFF3' }}>
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                    Report Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={newReport.type}
                    onChange={(e) => setNewReport({ ...newReport, type: e.target.value })}
                    className="border-2 focus:border-[#006600]"
                  >
                    <option value="security">Security</option>
                    <option value="compliance">Compliance</option>
                    <option value="access">Access</option>
                    <option value="infrastructure">Infrastructure</option>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                    Period
                  </label>
                  <Input
                    value={newReport.period}
                    onChange={(e) => setNewReport({ ...newReport, period: e.target.value })}
                    placeholder="e.g., Q1 2024"
                    className="border-2 focus:border-[#006600]"
                  />
                </div>
              </div>

              {/* Auditor */}
              <div className="pb-4 border-b" style={{ borderColor: '#E5EFF3' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                  Auditor <span className="text-red-500">*</span>
                </label>
                <Input
                  value={newReport.auditor}
                  onChange={(e) => setNewReport({ ...newReport, auditor: e.target.value })}
                  placeholder="Enter auditor name"
                  className="border-2 focus:border-[#006600]"
                />
              </div>

              {/* Scope */}
              <div className="pb-4 border-b" style={{ borderColor: '#E5EFF3' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                  Scope
                </label>
                <Textarea
                  value={newReport.scope}
                  onChange={(e) => setNewReport({ ...newReport, scope: e.target.value })}
                  placeholder="Define audit scope"
                  rows={2}
                  className="border-2 focus:border-[#006600]"
                />
              </div>

              {/* Objectives */}
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                  Objectives
                </label>
                <Textarea
                  value={newReport.objectives}
                  onChange={(e) => setNewReport({ ...newReport, objectives: e.target.value })}
                  placeholder="Define audit objectives"
                  rows={2}
                  className="border-2 focus:border-[#006600]"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-[#F8FCFB] rounded-b-2xl flex justify-end gap-3" style={{ borderColor: '#E5EFF3' }}>
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-2"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateReport}
              >
                Create Report
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {showViewModal && selectedReport && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowViewModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 border-2 border-[#E5EFF3] dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b" style={{ borderColor: '#E5EFF3', background: 'linear-gradient(145deg, #FFFFFF, #F8FCFB)' }}>
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold" style={{ color: '#0B2530' }}>
                  {selectedReport.title}
                </h2>
                <button 
                  onClick={() => setShowViewModal(false)}
                  className="transition-colors"
                  style={{ color: '#8A9AA2' }}
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Report Content */}
            <div className="p-6 space-y-5 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Status and Type */}
              <div className="grid grid-cols-2 gap-6 pb-5 border-b" style={{ borderColor: '#E5EFF3' }}>
                <div className="bg-[#F8FCFB] dark:bg-gray-900 p-4 rounded-lg border" style={{ borderColor: '#E5EFF3' }}>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#4A5B63' }}>
                    Status
                  </label>
                  <div>
                    {getStatusBadge(selectedReport.status)}
                  </div>
                </div>
                <div className="bg-[#F8FCFB] dark:bg-gray-900 p-4 rounded-lg border" style={{ borderColor: '#E5EFF3' }}>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#4A5B63' }}>
                    Type
                  </label>
                  <div>
                    {getTypeBadge(selectedReport.type)}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="pb-5 border-b" style={{ borderColor: '#E5EFF3' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                  Description
                </label>
                <p className="dark:text-gray-300 bg-[#F8FCFB] dark:bg-gray-900 p-4 rounded-lg border" style={{ color: '#4A5B63', borderColor: '#E5EFF3' }}>
                  {selectedReport.description}
                </p>
              </div>

              {/* Period and Auditor */}
              <div className="grid grid-cols-2 gap-6 pb-5 border-b" style={{ borderColor: '#E5EFF3' }}>
                <div className="bg-[#E8FCF7] p-4 rounded-lg border" style={{ borderColor: '#BFECDD' }}>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#006600' }}>
                    <Calendar size={16} />
                    Period
                  </label>
                  <p className="font-medium" style={{ color: '#0B2530' }}>{selectedReport.period}</p>
                </div>
                <div className="bg-[#F8FCFB] p-4 rounded-lg border" style={{ borderColor: '#E5EFF3' }}>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#0B2530' }}>
                    <Users size={16} />
                    Auditor
                  </label>
                  <p className="font-medium" style={{ color: '#0B2530' }}>{selectedReport.auditor}</p>
                </div>
              </div>

              {/* Findings Count */}
              <div className="pb-5 border-b" style={{ borderColor: '#E5EFF3' }}>
                <div className="bg-[#FFE7E0] p-4 rounded-lg border" style={{ borderColor: '#F2C9BC' }}>
                  <label className="block text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#BB0000' }}>
                    <BarChart3 size={16} />
                    Findings Count
                  </label>
                  <p className="text-2xl font-bold" style={{ color: '#0B2530' }}>{selectedReport.findingsCount}</p>
                </div>
              </div>

              {/* Summary */}
              <div className="pb-5 border-b" style={{ borderColor: '#E5EFF3' }}>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#0B2530' }}>
                  Summary
                </label>
                <p className="dark:text-gray-300 bg-[#E8FCF7] p-4 rounded-lg border" style={{ color: '#4A5B63', borderColor: '#BFECDD' }}>
                  {selectedReport.summary}
                </p>
              </div>

              {/* Generated At */}
              <div className="bg-[#F8FCFB] dark:bg-gray-900 p-4 rounded-lg border" style={{ borderColor: '#E5EFF3' }}>
                <label className="block text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: '#4A5B63' }}>
                  <Clock size={16} />
                  Generated At
                </label>
                <p className="font-medium" style={{ color: '#0B2530' }}>
                  {new Date(selectedReport.generatedAt).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-[#F8FCFB] rounded-b-2xl flex justify-end gap-3" style={{ borderColor: '#E5EFF3' }}>
              <Button
                variant="outline"
                onClick={() => setShowViewModal(false)}
                className="border-2"
              >
                Close
              </Button>
              <Button 
                className="flex items-center gap-2"
                onClick={() => handleDownloadReport(selectedReport)}
              >
                <Download size={16} />
                Download Report
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* More Filters Modal */}
      {showMoreFilters && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowMoreFilters(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full mx-4 border-2 border-[#E5EFF3]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: '#E5EFF3' }}>
              <h2 className="text-2xl font-bold" style={{ color: '#0B2530' }}>
                Filter Reports
              </h2>
              <button 
                onClick={() => setShowMoreFilters(false)}
                className="transition-colors"
                style={{ color: '#8A9AA2' }}
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status Filter */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: '#0B2530' }}>
                  Report Status
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterStatus === 'all'
                        ? 'border-[#006600] bg-[#E8FCF7] text-[#006600]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#006600]'
                    }`}
                  >
                    All Status
                  </button>
                  <button
                    onClick={() => setFilterStatus('completed')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterStatus === 'completed'
                        ? 'border-[#006600] bg-[#E8FCF7] text-[#006600]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#006600]'
                    }`}
                  >
                    <CheckCircle size={16} className="inline mr-1" />
                    Completed
                  </button>
                  <button
                    onClick={() => setFilterStatus('in_progress')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterStatus === 'in_progress'
                        ? 'border-[#0B2530] bg-[#F8FCFB] text-[#0B2530]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#0B2530]'
                    }`}
                  >
                    <Clock size={16} className="inline mr-1" />
                    In Progress
                  </button>
                  <button
                    onClick={() => setFilterStatus('draft')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterStatus === 'draft'
                        ? 'border-[#BB0000] bg-[#FFE7E0] text-[#BB0000]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#BB0000]'
                    }`}
                  >
                    <Edit size={16} className="inline mr-1" />
                    Draft
                  </button>
                </div>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-semibold mb-3" style={{ color: '#0B2530' }}>
                  Report Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setFilterType('all')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterType === 'all'
                        ? 'border-[#006600] bg-[#E8FCF7] text-[#006600]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#006600]'
                    }`}
                  >
                    All Types
                  </button>
                  <button
                    onClick={() => setFilterType('security')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterType === 'security'
                        ? 'border-[#BB0000] bg-[#FFE7E0] text-[#BB0000]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#BB0000]'
                    }`}
                  >
                    <Shield size={16} className="inline mr-1" />
                    Security
                  </button>
                  <button
                    onClick={() => setFilterType('compliance')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterType === 'compliance'
                        ? 'border-[#006600] bg-[#E8FCF7] text-[#006600]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#006600]'
                    }`}
                  >
                    <FileText size={16} className="inline mr-1" />
                    Compliance
                  </button>
                  <button
                    onClick={() => setFilterType('access')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterType === 'access'
                        ? 'border-[#0B2530] bg-[#F8FCFB] text-[#0B2530]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#0B2530]'
                    }`}
                  >
                    <Users size={16} className="inline mr-1" />
                    Access
                  </button>
                  <button
                    onClick={() => setFilterType('infrastructure')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      filterType === 'infrastructure'
                        ? 'border-[#006600] bg-[#E8FCF7] text-[#006600]'
                        : 'border-[#E2E8F0] text-[#0B2530] hover:border-[#006600]'
                    }`}
                  >
                    <Shield size={16} className="inline mr-1" />
                    Infrastructure
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t flex items-center justify-between" style={{ borderColor: '#E5EFF3' }}>
              <Button 
                variant="outline"
                onClick={() => {
                  setShowMoreFilters(false);
                }}
              >
                Apply Filters
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setFilterStatus('all');
                  setFilterType('all');
                  setShowMoreFilters(false);
                }}
              >
                Reset
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceReports;
